// Scrolls the contact form into view for a recognized `?ref=` and tells the
// form when it has arrived, so the subject can fade in once the form is on
// screen. Plain functions over injected window/document so the timing can be
// tested in Node without a browser.

/** Fired on `window` once the form has been brought into view. */
export const CONTACT_REF_SCROLLED_EVENT = "contact-ref-scrolled";

/** Smooth scrolling in every engine finishes well inside this. */
export const SMOOTH_SCROLL_MS = 1000;
/** A second look, for fonts and images that settle after the first one. */
export const LATE_SETTLE_MS = 2200;
/** How far the form may sit from where it was put before it is nudged back. */
const DRIFT_TOLERANCE_PX = 4;
/** Any of these means the visitor is driving the page; stop nudging. */
const USER_INPUT_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"] as const;

export interface ScrollTarget {
    scrollIntoView(options: ScrollIntoViewOptions): void;
    getBoundingClientRect(): { top: number };
}

export interface ScrollWindow {
    addEventListener(type: string, listener: () => void, options?: AddEventListenerOptions): void;
    removeEventListener(type: string, listener: () => void): void;
    dispatchEvent(event: Event): boolean;
    requestAnimationFrame(callback: () => void): number;
    cancelAnimationFrame(handle: number): void;
    setTimeout(callback: () => void, ms: number): number;
    clearTimeout(handle: number): void;
    getComputedStyle(target: ScrollTarget): { scrollMarginTop: string };
}

export interface ScrollDocument {
    readonly readyState: DocumentReadyState;
    readonly visibilityState: DocumentVisibilityState;
    addEventListener(type: string, listener: () => void, options?: AddEventListenerOptions): void;
    removeEventListener(type: string, listener: () => void): void;
    getElementById(id: string): ScrollTarget | null;
}

/**
 * Brings `targetId` into view and keeps it there while the page finishes
 * loading. Returns a cancel function for the effect cleanup.
 *
 * - Visible page: one animation frame for layout to settle, then a smooth
 *   scroll. After it lands, and again a little later, the form is nudged back
 *   into place (instantly) if fonts or images shifted the layout above it,
 *   unless the visitor has started scrolling themselves.
 * - Hidden page (link opened in a background tab): animation frames do not
 *   run and smooth scrolling cannot animate, so nothing happens until the tab
 *   is shown, and then the page simply opens at the form.
 */
export function scrollToContactRef(targetId: string, win: ScrollWindow, doc: ScrollDocument): () => void {
    let cancelled = false;
    let userTookOver = false;
    let announced = false;
    let frame: number | null = null;
    const timers = new Set<number>();
    const cleanups: Array<() => void> = [];

    const later = (ms: number, fn: () => void) => {
        const id = win.setTimeout(() => {
            timers.delete(id);
            if (!cancelled) fn();
        }, ms);
        timers.add(id);
    };

    const target = () => doc.getElementById(targetId);

    const scroll = (behavior: ScrollBehavior) => {
        target()?.scrollIntoView({ behavior, block: "start" });
    };

    // How far the form's top sits from where `scrollIntoView` puts it, which
    // is its `scroll-margin-top` below the viewport edge.
    const drift = () => {
        const el = target();
        if (!el) return 0;
        const margin = parseFloat(win.getComputedStyle(el).scrollMarginTop) || 0;
        return el.getBoundingClientRect().top - margin;
    };

    const nudge = () => {
        if (userTookOver) return;
        if (Math.abs(drift()) > DRIFT_TOLERANCE_PX) scroll("instant");
    };

    const announce = () => {
        if (announced) return;
        announced = true;
        win.dispatchEvent(new Event(CONTACT_REF_SCROLLED_EVENT));
    };

    const onUserInput = () => { userTookOver = true; };
    for (const type of USER_INPUT_EVENTS) {
        win.addEventListener(type, onUserInput, { passive: true });
        cleanups.push(() => win.removeEventListener(type, onUserInput));
    }

    const settleAfterLoad = () => {
        if (doc.readyState === "complete") return;
        const onLoad = () => later(100, nudge);
        win.addEventListener("load", onLoad, { once: true });
        cleanups.push(() => win.removeEventListener("load", onLoad));
    };

    const goVisible = () => {
        if (cancelled) return;
        scroll("smooth");
        later(SMOOTH_SCROLL_MS, () => { nudge(); announce(); });
        later(LATE_SETTLE_MS, nudge);
        settleAfterLoad();
    };

    const goWhenShown = () => {
        if (cancelled) return;
        scroll("instant");
        announce();
        later(LATE_SETTLE_MS, nudge);
        settleAfterLoad();
    };

    if (doc.visibilityState === "hidden") {
        const onVisibility = () => {
            if (doc.visibilityState === "hidden") return;
            doc.removeEventListener("visibilitychange", onVisibility);
            goWhenShown();
        };
        doc.addEventListener("visibilitychange", onVisibility);
        cleanups.push(() => doc.removeEventListener("visibilitychange", onVisibility));
    } else {
        frame = win.requestAnimationFrame(() => {
            frame = null;
            goVisible();
        });
    }

    return () => {
        cancelled = true;
        if (frame !== null) win.cancelAnimationFrame(frame);
        for (const id of timers) win.clearTimeout(id);
        timers.clear();
        for (const undo of cleanups) undo();
    };
}
